import React from 'react';
import { Box, Tooltip, keyframes } from '@mui/material';
import { useAppSelector } from '../../hooks';
import { ConnectionStatus } from '../../../features/connection-status/connection-status-slice';

// Subtle pulse used while (re)connecting so the dot reads as "working on it".
const pulse = keyframes`
    0%   { opacity: 1; }
    50%  { opacity: 0.3; }
    100% { opacity: 1; }
`;

// Shared color/label config so the app-bar dot and the drawer row stay in sync.
export const connectionStatusConfig: Record<
    ConnectionStatus,
    { color: string; label: string; pulsing: boolean }
> = {
    connected: {
        color: '#4caf50', // green
        label: 'Conexión en vivo activa',
        pulsing: false,
    },
    connecting: {
        color: '#ff9800', // amber
        label: 'Reconectando…',
        pulsing: true,
    },
    disconnected: {
        color: '#f44336', // red
        label: 'Sin conexión en vivo — los datos podrían estar desactualizados',
        pulsing: false,
    },
};

// Presentational dot, reused by the app bar (with tooltip) and the drawer row.
export const ConnectionDot = (props: { color: string; pulsing: boolean }) => {
    const { color, pulsing } = props;
    return (
        <Box
            sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: color,
                flexShrink: 0,
                boxShadow: `0 0 6px ${color}`,
                animation: pulsing
                    ? `${pulse} 1.2s ease-in-out infinite`
                    : 'none',
            }}
        />
    );
};

export const ConnectionStatusIndicator = () => {
    const status = useAppSelector((state) => state.connectionStatus.status);
    const { color, label, pulsing } = connectionStatusConfig[status];

    return (
        <Tooltip title={label}>
            <Box sx={{ display: 'flex', alignItems: 'center', mx: 1 }}>
                <ConnectionDot color={color} pulsing={pulsing} />
            </Box>
        </Tooltip>
    );
};
